import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Snackbar,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import { getStudentLandingThemeOverrides, landingThemeConfig } from './LandingStudent/styles/landingThemeConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MinimalFooter from './LandingStudent/components/MinimalFooter';
import api from '../utils/api';
import { links } from '../config/links';

const Contact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { } = useAppTheme();
  const [showNav, setShowNav] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: '',
    subject: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Get color theme from location state or localStorage
  const [colorTheme] = useState(() => {
    if (process.env.NODE_ENV === 'development') {
      return location.state?.colorTheme || localStorage.getItem('devColorTheme') || 'teal';
    }
    return 'teal';
  });

  // Determine which theme to use based on referrer
  const isFromEducators = location.state?.from === 'educators' || 
                          document.referrer.includes('/educators') || 
                          document.referrer.includes('/business');
  
  // Create theme based on context
  const theme = createTheme(
    isFromEducators ? {
      palette: {
        mode: 'light',
        ...landingThemeConfig.b2b,
      },
    } : getStudentLandingThemeOverrides(colorTheme)
  );

  React.useEffect(() => {
    const handleScroll = () => {
      setShowNav(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    if (!formData.userType) {
      errors.userType = 'Please select your role';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      errors.subject = 'Subject must be at least 5 characters';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await api.post('/contact/submit', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        user_type: formData.userType
      });
      
      if (response.data && response.data.success) {
        setSuccess(true);
        // Clear form
        setFormData({
          name: '',
          email: '',
          userType: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(response.data?.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send message. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const userTypes = [
    { value: 'student', label: 'Student' },
    { value: 'educator', label: 'Educator' },
    { value: 'tutor', label: 'Tutor' },
    { value: 'administrator', label: 'School Administrator' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(to bottom, ${theme.palette.background.default}ee, ${theme.palette.background.default}cc)`
            : `linear-gradient(to bottom, ${theme.palette.background.paper}ee, ${theme.palette.background.paper}cc)`,
          backdropFilter: 'blur(10px)',
          boxShadow: showNav ? 1 : 0,
          transition: 'all 0.3s ease',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 70, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                onClick={handleBackClick}
                sx={{ color: theme.palette.text.primary }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                }}
                onClick={() => navigate('/')}
              >
                examr
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        pt: 12, 
        pb: 6, 
        backgroundColor: theme.palette.background.default, 
        minHeight: '100vh' 
      }}>
        <Container maxWidth="md">
          <Paper 
            elevation={theme.palette.mode === 'dark' ? 0 : 2} 
            sx={{ 
              p: { xs: 3, md: 5 },
              backgroundColor: theme.palette.mode === 'dark' 
                ? theme.palette.background.paper 
                : theme.palette.background.paper,
              border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(45deg, ${theme.palette.text.primary} 30%, ${theme.palette.primary.main} 90%)`
                  : theme.palette.text.primary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.text.primary,
              }}
            >
              Contact Us
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Have a question, suggestion, or need help? We'd love to hear from you!
            </Typography>

            {success ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  Message Sent Successfully!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Thank you for contacting us. We'll get back to you within 1-2 business days.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setSuccess(false)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                  }}
                >
                  Send Another Message
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    name="name"
                    label="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    fullWidth
                    required
                  />
                  
                  <TextField
                    name="email"
                    label="Your Email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    fullWidth
                    required
                  />
                  
                  <TextField
                    name="userType"
                    label="I am a..."
                    select
                    value={formData.userType}
                    onChange={handleChange}
                    error={!!formErrors.userType}
                    helperText={formErrors.userType}
                    fullWidth
                    required
                  >
                    {userTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  
                  <TextField
                    name="subject"
                    label="Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    error={!!formErrors.subject}
                    helperText={formErrors.subject}
                    fullWidth
                    required
                  />
                  
                  <TextField
                    name="message"
                    label="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    error={!!formErrors.message}
                    helperText={formErrors.message}
                    multiline
                    rows={6}
                    fullWidth
                    required
                  />
                  
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    disabled={submitting}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Box>
              </form>
            )}
            
            <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                You can also reach us directly at{' '}
                <Box
                  component="a"
                  href={`mailto:${links.external.supportEmail}`}
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {links.external.supportEmail}
                </Box>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
      
      {/* Footer */}
      <MinimalFooter colorTheme={colorTheme} />
      
      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </ThemeProvider>
  );
};

export default Contact;